"use client";

import React, { useState, useTransition, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { sendMagicCode, verifyMagicCode, getOrgSsoConnections } from "../../../app/actions/auth-custom";
import { isDisposableEmailDomain, isPublicEmailDomain } from "@brev-io/common/src/utils/validation";
import { DISPOSABLE_EMAIL_ERROR, PUBLIC_EMAIL_ERROR, SSO_REQUIRED_ERROR, buildAuthHref, extractEmailDomain } from "../../lib/auth-constants";
import { sanitizeReturnTo } from "../../lib/auth/return-to";
import { oauthLandingPath } from "../../lib/auth/invitation-carry";
import type { AuthActionResult, SsoConnectionInfo } from "../../lib/auth-types";
import MagicCodeInput from "./MagicCodeInput";
import EmailVerificationForm from "./EmailVerificationForm";
import MfaChallengeForm from "./MfaChallengeForm";
import MfaEnrollmentForm from "./MfaEnrollmentForm";
import SocialOAuthButtons from "./SocialOAuthButtons";
import ErrorAlert from "./ErrorAlert";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { authFailureKind, normalizeAuthError } from "@/lib/analytics/auth-errors";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Note } from "@/components/ui/note";

const AUTH_SUCCESS_REDIRECT = "/views/company-goals";
const STORED_EMAIL_KEY = "brev_login_email";

type PendingState =
  | { type: "email-verification"; token: string; email: string }
  | { type: "mfa-challenge"; token: string; challengeId: string }
  | { type: "mfa-enrollment"; token: string; challengeId: string; totpUri: string; totpSecret: string };

function getStoredEmail(): string {
  try {
    return localStorage.getItem(STORED_EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

function storeEmail(email: string) {
  try {
    localStorage.setItem(STORED_EMAIL_KEY, email);
  } catch {
    // localStorage unavailable
  }
}

function clearStoredEmail() {
  try {
    localStorage.removeItem(STORED_EMAIL_KEY);
  } catch {
    // localStorage unavailable
  }
}

function extractDomain(email: string): string | null {
  const domain = extractEmailDomain(email);
  // SSO lookup needs a valid TLD; reject single-segment domains
  if (!domain || !domain.includes(".")) return null;
  return domain;
}

function getSsoButtonLabel(conn: SsoConnectionInfo): string {
  // Use the connection name if it looks like a custom name, otherwise derive from type
  if (conn.name && conn.name !== conn.type) {
    return `Continue with ${conn.name}`;
  }
  // Derive a readable label from the connection type
  const typeLabels: Record<string, string> = {
    OktaSAML: "Okta SSO",
    AzureSAML: "Azure AD SSO",
    GoogleSAML: "Google SAML SSO",
    GenericSAML: "SSO",
    GenericOIDC: "SSO",
    ADFSSAML: "AD FS SSO",
    OneLoginSAML: "OneLogin SSO",
    PingOneSAML: "PingOne SSO",
    JumpCloudSAML: "JumpCloud SSO",
  };
  return `Continue with ${typeLabels[conn.type] ?? "SSO"}`;
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 py-1">
      <Separator className="flex-1" />
      <Note as="span" className="uppercase">or</Note>
      <Separator className="flex-1" />
    </div>
  );
}

interface LoginFormProps {
  googleUrl: string;
  /** Optional — see SocialOAuthButtons. */
  microsoftUrl?: string;
  /** Optional — see SocialOAuthButtons. */
  slackUrl?: string;
  invitationToken?: string;
  /** BRE-6058: `/signup?code=` preset code, carried on so the link back keeps it. */
  signupPresetCode?: string;
  /** BRE-7698: the sanitized link to land on after sign-in, instead of the Organization view. */
  returnTo?: string;
}

export default function LoginForm({ googleUrl, microsoftUrl, slackUrl, invitationToken, signupPresetCode, returnTo }: LoginFormProps) {
  const signupHref = buildAuthHref("/signup", { invitationToken, signupPresetCode, returnTo });
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [errorSupportRef, setErrorSupportRef] = useState<string | undefined>(undefined);

  // Single entry point for updating the error alert so the support-ref code
  // (shown when Radar blocks an attempt) stays in sync with the message. New
  // errors default to no ref; Radar blocks pass a ref derived from the WorkOS
  // attemptId so the Contact Support button gives support a lookup handle.
  const showError = useCallback((message: string, supportRef?: string) => {
    setError(message);
    setErrorSupportRef(supportRef);
  }, []);
  const [magicCodeSent, setMagicCodeSent] = useState(false);
  const [magicAuthChallengeId, setMagicAuthChallengeId] = useState<string | undefined>();
  const [pendingVerification, setPendingVerification] = useState<PendingState | null>(null);

  // SSO lookup result, bound to the domain it was fetched for: a lookup that
  // resolves after the user has moved on to another domain must not render as
  // that domain's SSO option (BRE-6667).
  const [ssoLookup, setSsoLookup] = useState<{ domain: string; connections: SsoConnectionInfo[] } | null>(null);
  const lastLookedUpDomain = useRef<string | null>(null);
  const currentDomain = extractDomain(email);
  const ssoConnections = ssoLookup && ssoLookup.domain === currentDomain ? ssoLookup.connections : [];
  // BRE-8955: the org requires SSO through Brev, so the email code is not offered.
  const ssoRequired = ssoConnections.some((conn) => conn.ssoRequired);

  // Load stored email on mount
  useEffect(() => {
    const stored = getStoredEmail();
    if (stored) {
      setEmail(stored);
    }
  }, []);

  const lookupSsoConnections = useCallback(async (domain: string) => {
    if (domain === lastLookedUpDomain.current) return;
    lastLookedUpDomain.current = domain;
    try {
      const connections = await getOrgSsoConnections(domain, oauthLandingPath({ invitationToken, returnTo }));
      if (domain !== lastLookedUpDomain.current) return;
      setSsoLookup({ domain, connections });
    } catch {
      if (domain !== lastLookedUpDomain.current) return;
      setSsoLookup({ domain, connections: [] });
    }
  }, [invitationToken, returnTo]);

  // Fetch SSO connections when email domain changes (debounced to avoid rapid lookups)
  useEffect(() => {
    const domain = extractDomain(email);
    if (!domain) {
      setSsoLookup(null);
      lastLookedUpDomain.current = null;
      return;
    }
    if (domain === lastLookedUpDomain.current) return;

    const timer = setTimeout(() => {
      lookupSsoConnections(domain);
    }, 500);
    return () => clearTimeout(timer);
  }, [email, lookupSsoConnections]);

  function navigateOnSuccess() {
    // A full load, because the link may be a Route Handler (`/oauth2/init`
    // redirects off-site), which a client-side navigation cannot land on.
    const landing = sanitizeReturnTo(returnTo);
    if (landing) {
      window.location.assign(landing);
      return;
    }
    router.push(AUTH_SUCCESS_REDIRECT);
    router.refresh();
  }

  function handleResult(result: AuthActionResult, method: string) {
    if (result.success) {
      captureProductClientEvent(ANALYTICS_EVENTS.AUTH_LOGIN, { method });
      navigateOnSuccess();
      return;
    }
    if ("signInUrl" in result && result.signInUrl) {
      // BRE-8618: the org this sign-in lands in requires its own sign-in (SSO).
      window.location.assign(result.signInUrl);
      return;
    }
    if ("pendingAction" in result) {
      if (result.pendingAction === "email-verification") {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_EMAIL_VERIFICATION_REQUIRED, { method });
        setPendingVerification({ type: "email-verification", token: result.pendingToken, email: result.email });
        return;
      }
      if (result.pendingAction === "mfa-challenge") {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_CHALLENGED, { method });
        setPendingVerification({ type: "mfa-challenge", token: result.pendingToken, challengeId: result.challengeId });
        return;
      }
      if (result.pendingAction === "mfa-enrollment") {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_CHALLENGED, { method, mfa_type: "enrollment" });
        setPendingVerification({
          type: "mfa-enrollment",
          token: result.pendingToken,
          challengeId: result.challengeId,
          totpUri: result.totpUri,
          totpSecret: result.totpSecret,
        });
        return;
      }
    }
    // Prefer the structural Radar signal (blockedKind) over string-matching the
    // user-facing copy; fall back to normalizeAuthError otherwise.
    const errorCode = result.blockedKind ? "radar_blocked" : normalizeAuthError(result.error);
    captureProductClientEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
      method,
      error_code: errorCode,
      failure_kind: authFailureKind(errorCode),
    });
    showError(result.error, result.supportRef);
  }

  // Client-side mirror of the server's rejectBlockedEmail — catches both
  // personal (gmail.com) and disposable (mailinator.com, spotshops.com) domains
  // before the server roundtrip so the user gets an instant inline error.
  function getBlockedEmailError(emailValue: string): string | null {
    const d = extractDomain(emailValue);
    if (!d) return null;
    if (isPublicEmailDomain(d)) return PUBLIC_EMAIL_ERROR;
    if (isDisposableEmailDomain(d)) return DISPOSABLE_EMAIL_ERROR;
    return null;
  }

  function handleMagicSend(e: React.FormEvent) {
    e.preventDefault();
    showError("");
    const blockedError = getBlockedEmailError(email);
    if (blockedError) { showError(blockedError); return; }
    // The submit button is hidden, but Enter in the email field still submits.
    if (ssoRequired) { showError(SSO_REQUIRED_ERROR); return; }
    storeEmail(email);
    startTransition(async () => {
      try {
        const result = await sendMagicCode(email, "login", invitationToken);
        if (result.success) {
          captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MAGIC_CODE_SENT);
          setMagicAuthChallengeId(result.magicAuthChallengeId);
          setMagicCodeSent(true);
        } else if ("error" in result) {
          captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MAGIC_CODE_FAILED, { error_code: normalizeAuthError(result.error) });
          showError(result.error, result.supportRef);
        }
      } catch {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MAGIC_CODE_FAILED, { error_code: "connection_lost" });
        showError("Connection lost. Please refresh the page and try again.");
      }
    });
  }

  function handleMagicVerify(code: string) {
    showError("");
    startTransition(async () => {
      try {
        const result = await verifyMagicCode(
          email,
          code,
          magicAuthChallengeId,
          "login",
          invitationToken,
          oauthLandingPath({ invitationToken, returnTo }),
        );
        if (!result.success && "error" in result && result.error.includes("SSO") && !result.signInUrl) {
          // Reset to main form so SSO buttons are visible
          setMagicCodeSent(false);
        }
        handleResult(result, "magic_code");
      } catch {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, { method: "magic_code", error_code: "connection_lost" });
        showError("Connection lost. Please refresh the page and try again.");
      }
    });
  }

  // BRE-8955: a pending step was refused because the org requires SSO; go back
  // to the main form and look the domain up again so its SSO button shows.
  function handleSsoRequired() {
    setPendingVerification(null);
    setMagicCodeSent(false);
    showError(SSO_REQUIRED_ERROR);
    if (currentDomain) {
      lastLookedUpDomain.current = null;
      void lookupSsoConnections(currentDomain);
    }
  }

  function handleClearEmail() {
    clearStoredEmail();
    setEmail("");
    setSsoLookup(null);
    lastLookedUpDomain.current = null;
    showError("");
  }

  // Show email verification form
  if (pendingVerification?.type === "email-verification") {
    return (
      <EmailVerificationForm
        pendingToken={pendingVerification.token}
        email={pendingVerification.email}
        flow="login"
        onSuccess={navigateOnSuccess}
        onPendingState={setPendingVerification}
        returnTo={oauthLandingPath({ invitationToken, returnTo })}
        onSsoRequired={handleSsoRequired}
      />
    );
  }

  // Show MFA challenge form
  if (pendingVerification?.type === "mfa-challenge") {
    return (
      <MfaChallengeForm
        pendingToken={pendingVerification.token}
        challengeId={pendingVerification.challengeId}
        onSuccess={navigateOnSuccess}
        returnTo={oauthLandingPath({ invitationToken, returnTo })}
        onSsoRequired={handleSsoRequired}
      />
    );
  }

  // Show MFA enrollment form
  if (pendingVerification?.type === "mfa-enrollment") {
    return (
      <MfaEnrollmentForm
        pendingToken={pendingVerification.token}
        challengeId={pendingVerification.challengeId}
        totpUri={pendingVerification.totpUri}
        totpSecret={pendingVerification.totpSecret}
        onSuccess={navigateOnSuccess}
        returnTo={oauthLandingPath({ invitationToken, returnTo })}
        onSsoRequired={handleSsoRequired}
      />
    );
  }

  // Magic code verification step
  if (magicCodeSent) {
    return (
      <div className="space-y-4 text-sm">
        <Note size="sm">
          We sent a 6-digit code to <span className="font-medium">{email}</span>
        </Note>
        <MagicCodeInput onSubmit={handleMagicVerify} isPending={isPending} />
        {error && <ErrorAlert message={error} supportRef={errorSupportRef} />}
        <Button
          variant="link"
          type="button"
          onClick={() => { setMagicCodeSent(false); showError(""); }}
          data-testid="login-use-different-email"
          size="inline"
          className="rounded-sm"
        >
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <ErrorAlert message={error} supportRef={errorSupportRef} />}

      {/* Google, Microsoft, Slack OAuth. Hidden once the typed domain requires
          SSO, since the callback refuses them for it (BRE-8955). */}
      {!ssoRequired && (
        <>
          <SocialOAuthButtons googleUrl={googleUrl} microsoftUrl={microsoftUrl} slackUrl={slackUrl} />
          <OrDivider />
        </>
      )}

      {/* Org SSO connections, offered on top once discovered for the domain.
          The email code stays available unless the org requires SSO through
          Brev (BRE-8955); WorkOS's own policy is enforced at verify time with
          sso_required (BRE-6667). */}
      {ssoConnections.length > 0 && (
        <>
          {ssoConnections.map((conn) => (
            <Button key={conn.id} asChild variant="outline" className="relative flex w-full">
              <a
                href={conn.authUrl}
                data-testid={`sso-connection-${conn.id}`}
                onClick={() => captureProductClientEvent(ANALYTICS_EVENTS.AUTH_SSO_REDIRECT, { sso_type: conn.type })}
              >
                <svg className="absolute left-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span className="min-w-0 flex-1 truncate px-6 text-center" title={getSsoButtonLabel(conn)}>{getSsoButtonLabel(conn)}</span>
              </a>
            </Button>
          ))}
          <Note className="text-center" data-testid="login-sso-caption">
            {ssoRequired ? "Your organization requires SSO sign-in." : "Your organization supports SSO sign-in."}
          </Note>
        </>
      )}

      {/* Magic link is the primary self-service credential; it stays mounted
          through the SSO lookup because it holds the page's only email input. */}
      <form onSubmit={handleMagicSend} className="space-y-3">
        <label htmlFor="login-magic-email" className="sr-only">Email address</label>
        <Input
          id="login-magic-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          required
          autoComplete="username"
          autoCapitalize="off"
          testId="login-magic-email-input"
          variant="onboarding"
          inputClassName="px-0"
        />
        {!ssoRequired && (
          <Button
            type="submit"
            disabled={isPending}
            data-testid="login-magic-submit-button"
            className="w-full"
          >
            {isPending ? "Sending code..." : "Email me a sign-in code"}
          </Button>
        )}
      </form>

      {email && (
        <div className="text-center text-sm">
          <Button
            variant="quiet"
            type="button"
            onClick={handleClearEmail}
            data-testid="login-clear-email"
            size="inline"
            className="hover:underline"
          >
            Use a different account
          </Button>
        </div>
      )}

      <div className="pt-2 text-center">
        <Note as="span" size="sm">
          Don&apos;t have an account?{" "}
          <Button variant="link" size="inline" asChild>
            <Link href={signupHref} data-testid="login-signup-link">
              Sign up
            </Link>
          </Button>
        </Note>
      </div>
    </div>
  );
}
